/* eslint-disable max-statements */
/* global document, process */
// import '@activeadmin/activeadmin'; // eventos AA

import Vue from 'vue/dist/vue.esm'; // runtime + compiler (necesario para #app con templates del DOM)
import Vuex from 'vuex';
import VueI18n from 'vue-i18n';
import Vue2Filters from 'vue2-filters';
import VModal from 'vue-js-modal';
import VCalendar from 'v-calendar';
import VeeValidate from 'vee-validate';
import vSelect from 'vue-select';
// ⚠️ OJO: NO importamos 'vue-select/dist/vue-select.css' para evitar el error de resolución.
// Si quieres estilos, ver nota al final.

import * as VueGoogleMaps from 'vue2-google-maps';
import VueGtm from 'vue-gtm';
import TurbolinksAdapter from 'vue-turbolinks';
import VueMoment from 'vue-moment';

// Componentes (los que sabemos que existen)
import Home from './components/home.vue';
import ContactInformation from './components/profile/contact-information.vue';
import BillingInformation from './components/profile/billing-information.vue';
import Security from './components/profile/security.vue';
import OurHistory from './components/our-history.vue';
import SignIn from './components/sign-in.vue';
import SignInOverlay from './components/sign-in-overlay.vue';
import CommercialCalendar from './components/commercial-calendar.vue';
import ProductionCalendar from './components/production-calendar.vue';
import MapForm from './components/map-form.vue';
import ModalPayment from './components/modal-payment.vue';
import BulkUploadModal from './components/profile/bulk-upload-modal.vue';
import NewOrder from './components/order/new.vue';
import VueCarousel from 'vue-carousel';
import moment from 'moment';
// Estado / i18n / vars
import Locales from './locales/locales';
import store from './components/store';
import vars from './components/vars'; // si lo usas en otros módulos

// ---- Config plugins ----
const mapConfiguration = {
  installComponents: true,
  load: {
    key: vars.googleMapsKey,
    libraries: 'places',
  },
};

const validateConfiguration = {
  locale: 'es',
  dictionary: {
    es: {
      messages: {
        alpha_num: 'Sólo puede usar números y letras.',
        alpha_spaces: 'Sólo puede usar letras y espacios.',
        email: 'No es un email válido.',
        max: 'No cumple el tamaño máximo establecido.',
        min: 'No cumple el tamaño mínimo establecido.',
        numeric: 'Sólo puede usar números.',
        required: 'Este campo es obligatorio.',
      },
    },
  },
};

Vue.config.productionTip = false;

// ------- Carga opcional de componentes que podrían no existir -------
// Evita romper el build si el archivo fue movido/renombrado.

// Intentamos encontrar "new-order" en rutas comunes; si no está, no se registra.
// Mapa tag -> Componente (los seguros + los opcionales si existen)
const COMPONENTS_MAP_BASE = {
  'home': Home,
  'new-order': NewOrder,
  'our-history': OurHistory,
  'sign-in': SignIn,
  'sign-in-overlay': SignInOverlay,
  'contact-information': ContactInformation,
  'billing-information': BillingInformation,
  'security': Security,
  'map-form': MapForm,
  'modal-payment': ModalPayment,
  'bulk-upload-modal': BulkUploadModal,
  'commercial-calendar': CommercialCalendar,
  'production-calendar': ProductionCalendar,
};

// También los registro globalmente (útil para el modo #app con plantillas en el DOM)
Object.entries(COMPONENTS_MAP_BASE).forEach(([name, cmp]) => Vue.component(name, cmp));

// Guardamos instancias para limpiar en navegación
let __vueInstances__ = [];
let __rootVm__ = null;

// Inicializa plugins una sola vez (idempotente)
let __pluginsReady__ = false;
function ensurePlugins() {
  if (__pluginsReady__) return;

  Vue.use(TurbolinksAdapter);
  Vue.use(VueI18n);
  Vue.use(Vuex);
  Vue.use(VueGoogleMaps, mapConfiguration);
  Vue.use(VueMoment, { moment });
  Vue.use(Vue2Filters);
  Vue.use(VeeValidate, validateConfiguration);
  Vue.use(VModal, { dynamic: true });
  Vue.use(vSelect);
  Vue.use(VueCarousel);
  Vue.use(VCalendar, {
    firstDayOfWeek: 2,
    locale: 'es',
    titlePosition: 'center',
    datePickerShowDayPopover: false,
    datePickerTintColor: '#50bfa3',
    themeStyles: {
      wrapper: {
        padding: '10px',
        margin: '30px',
        background: 'white',
        color: '#58585b',
        borderRadius: '6px',
        boxShadow: '0 2px 23px 0 #E4E6E9',
        fontFamily: 'Karla',
      },
      headerTitle: {
        fontSize: '0.9rem',
      },
      dayCellNotInMonth: {
        opacity: 0,
      },
    },
  });

  if (process.env.GTM_CONTAINER_ID) {
    Vue.use(VueGtm, { id: process.env.GTM_CONTAINER_ID });
  }

  __pluginsReady__ = true;
}

// i18n por página
function makeI18n() {
  return new VueI18n({
    locale: document.documentElement.lang || 'es',
    messages: Locales.messages,
  });
}

function cleanupInstances() {
  // Destruye instancias por-tag
  __vueInstances__.forEach((vm) => {
    try { vm.$destroy(); } catch (e) { /* noop */ }
  });
  __vueInstances__ = [];

  // Destruye root si existe
  if (__rootVm__) {
    try { __rootVm__.$destroy(); } catch (e) { /* noop */ }
    __rootVm__ = null;
  }

  // Limpia marcas en nodos
  const marked = document.querySelectorAll('[data-vue-mounted="true"]');
  marked.forEach((el) => el.removeAttribute('data-vue-mounted'));
}

// Modo 1: SPA clásica (#app) — igual que “Antes”
function mountRootApp() {
  const el = document.getElementById('app');
  if (!el) return false;

  ensurePlugins();
  const i18n = makeI18n();

  // Monta usando el DOM bajo #app
  __rootVm__ = new Vue({
    el: '#app',
    i18n,
    store,
  });

  el.setAttribute('data-vue-mounted', 'true');

  return true;
}

// Modo 2: por etiquetas — ideal para ActiveAdmin u otras vistas con componentes sueltos
function mountAllComponents(root = document) {
  ensurePlugins();
  const i18n = makeI18n();

  Object.entries(COMPONENTS_MAP_BASE).forEach(([tagName, Component]) => {
    const nodes = Array.from(root.querySelectorAll(tagName));
    nodes.forEach((el) => {
      if (el.getAttribute('data-vue-mounted') === 'true') return;

      const vm = new Vue({
        i18n,
        store,
        render: (h) => h(Component),
      });

      vm.$mount(el);
      el.setAttribute('data-vue-mounted', 'true');
      __vueInstances__.push(vm);
    });
  });
}

function safeMount() {
  try {
    cleanupInstances();

    // Si hay #app, usa root; si no, monta por etiquetas dentro de ActiveAdmin o documento
    const mountedRoot = mountRootApp();
    if (!mountedRoot) {
      const root = document.getElementById('active_admin_content') || document;
      mountAllComponents(root);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error montando Vue:', e);
  }
}

// Hooks de navegación / carga
function setupLifecycle() {
  document.addEventListener('turbolinks:before-render', cleanupInstances);
  document.addEventListener('turbolinks:load', safeMount);
  document.addEventListener('active_admin:loaded', safeMount);
  document.addEventListener('DOMContentLoaded', safeMount);
}

// Start
setupLifecycle();
